import React from 'react';
import PropTypes from 'prop-types';
import range from 'lodash/range';
import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';
import sortBy from 'lodash/sortBy';
import map from 'lodash/map';
import PieChartComponent from '@Components/Results/targets/pie-chart.component';
import ChartConstants from '@Constants/ChartConstants';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ColumnChartComponent from '@Components/Results/targets/column-chart.component';
import Entries from '@Services/Entries';

export default class TargetDetailsChartsComponent extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = { areChartsReady: false };
    this.charts = null;

    this.getBioassaysData = this.getBioassaysData.bind(this);
    this.getCellLinesData = this.getCellLinesData.bind(this);
    this.getLigandsData = this.getLigandsData.bind(this);
    this.prepareCharts = this.prepareCharts.bind(this);

    this.loadDetails();
  }

  getBioassaysData(items) {
    return this.preparePieData(items, ChartConstants.colors.orange);
  }

  getCellLinesData(items) {
    const correctedItems = this.preparePieChartItems(cloneDeep(items), ChartConstants.colors.blue.length);
    const cell = this.mapDataLabelsColors(correctedItems, ChartConstants.colors.blue);

    cell.options = cloneDeep(ChartConstants.pieChartOptions);
    return this.preparePieData(items, ChartConstants.colors.blue);
  }

  getLigandsData(items) {
    const barsCount = 15;
    const barItems = range(barsCount).map(() => ({ label: '', size: 0 }));

    (items || []).forEach((item) => {
      if (item.noData) {
        barItems[barItems.length - 1] = item;
      } else {
        barItems[item.start] = item;
      }
    });

    const ligands = this.mapDataLabelsColors(barItems, null, ChartConstants.colors.mainOrange);
    ligands.options = ChartConstants.barChartOptions;

    return ligands;
  }

  loadDetails() {
    const { metadata, target } = this.props;

    Entries.getTargetsDetails(target.index, { ...metadata, databaseId: target.databaseId })
      .then(this.prepareCharts)
      .catch(Logger.error);
  }

  prepareCharts(data) {
    this.charts = {
      bioassays: this.getBioassaysData(data.bioassays.pieItems),
      cells: this.getCellLinesData(data.cells.pieItems),
      ligands: this.getLigandsData(data.ligands.histogramItems),
    };
    this.setState({ areChartsReady: true });
  }

  preparePieData(items, colors) {
    const correctedItems = this.preparePieChartItems(cloneDeep(items), colors.length);
    const data = this.mapDataLabelsColors(correctedItems, colors);

    data.options = cloneDeep(ChartConstants.pieChartOptions);
    return data;
  }

  // eslint-disable-next-line class-methods-use-this
  mapDataLabelsColors(items, allColors, singleColor) {
    const colors = [];
    const labels = [];
    const data = [];
    const pairs = [];

    (items ? [...items] : []).forEach((item, index) => {
      labels.push(item.label);
      data.push(item.size);
      pairs.push({ name: item.label, y: item.size });

      if (!singleColor) {
        if (item.noData) {
          colors.push(ChartConstants.colors.white);
        } else {
          colors.push(allColors[index]);
        }
      }
    });

    return {
      colors: singleColor ? ChartConstants.colors.barChartColors : colors,
      labels,
      data,
      pairs,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  preparePieChartItems(items, allowedItemsCount) {
    const noDataItemIndex = findIndex(items, (item) => item.noData);

    // If colors count and items count are consistent just return items
    const itemsLength = noDataItemIndex !== -1 ? items.length - 1 : items.length;
    if (itemsLength <= allowedItemsCount) {
      return items;
    }

    // Get noData item and remove one from items
    const noDataItem = noDataItemIndex !== -1 ? items.splice(noDataItemIndex, 1)[0] : null;

    // save original order
    // eslint-disable-next-line no-param-reassign,no-return-assign
    (items || []).forEach((item, index) => (item.index = index));

    // Sort by items size
    // eslint-disable-next-line no-param-reassign
    items = sortBy(items, (item) => item.size);

    // Get top size items and revert original order; topItems is a head of final collection
    const topItems = sortBy(items.splice(-(allowedItemsCount - 1), allowedItemsCount - 1), (item) => item.index);

    // Revert original order of the rest items
    // eslint-disable-next-line no-param-reassign
    items = sortBy(items, (item) => item.index);

    // Generate others item
    const othersItem = { size: 0, label: 'Others: ', noData: false, other: true };
    items.forEach((item, index) => {
      othersItem.size += item.size;
      othersItem.label = [othersItem.label, item.label].join(index ? ', ' : '');
    });

    // Add others item to final collection
    topItems.push(othersItem);

    if (noDataItem) {
      topItems.push(noDataItem);
    }

    return topItems;
  }

  render() {
    const { areChartsReady } = this.state;

    return (
      <section className="target-details-charts">
        <h4 className="no-float">Distribution of bioactivities</h4>

        <section className="target-details-charts__pie-charts">
          <div className="target-details-charts__pie-chart over-bioassays">
            <h5 className="target-details-charts__title">Over bioassays</h5>

            {areChartsReady && this.charts.bioassays && (
              <div className="target-details-charts__chart-row">
                <section className="target-details-charts__chart-section">
                  <PieChartComponent
                    data={this.charts.bioassays.pairs}
                    colors={this.charts.bioassays.colors}
                    options={this.charts.bioassays.options}
                  />
                </section>

                <aside className="target-details-charts__chart-legend">
                  {map(this.charts.bioassays.labels, (label, index) => (
                    <div className="target-details-charts__chart-legend__item" key={index}>
                      <div
                        className="target-details-charts__chart-legend__item-indicator"
                        style={{ backgroundColor: this.charts.bioassays.colors[index] }}
                      />
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="target-details-charts__chart-legend__item-text">{label}</label>
                    </div>
                  ))}
                </aside>
              </div>
            )}
          </div>
          <div className="target-details-charts__pie-chart over-cell-lines">
            <h5 className="target-details-charts__title">Over cell lines</h5>

            {areChartsReady && this.charts.cells && (
              <div className="target-details-charts__chart-row">
                <section className="target-details-charts__chart-section">
                  <PieChartComponent data={this.charts.cells.pairs} colors={this.charts.cells.colors} options={this.charts.cells.options} />
                </section>

                <aside className="target-details-charts__chart-legend">
                  {map(this.charts.cells.labels, (label, index) => (
                    <div className="target-details-charts__chart-legend__item" key={index}>
                      <div
                        className="target-details-charts__chart-legend__item-indicator"
                        style={{ backgroundColor: this.charts.cells.colors[index] }}
                      />
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="target-details-charts__chart-legend__item-text">{label}</label>
                    </div>
                  ))}
                </aside>
              </div>
            )}
          </div>
        </section>

        {areChartsReady && this.charts.ligands && (
          <section className="target-details-charts__histogram-charts">
            <div className="target-details-charts__histogram-chart over-ligands">
              <h5 className="target-details-charts__title">Over ligands</h5>

              <section>
                <div className="rx-chart--wrapper rx-chart-custom-tooltip">
                  <ColumnChartComponent
                    height="20%"
                    values={this.charts.ligands.data}
                    categories={this.charts.ligands.labels}
                    colors={this.charts.ligands.colors}
                    options={this.charts.ligands.options}
                    yAxis="Compound count"
                  />
                </div>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="target-details-charts__histogram-charts__legend">Bioactivity ranges (based on pX(-log[activity])</label>
              </section>
            </div>
          </section>
        )}
      </section>
    );
  }
}

TargetDetailsChartsComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  target: PropTypes.object.isRequired,
};
